import useTestId from '@ori/testid-generator';
import { LastAddedItemMethod } from '@ori-events/basket-updated';
import { Products } from '@ori-ui/icons';
import { SHADOW_LEVEL3, styled } from '@ori-ui/mui';
import type { VFC } from 'react';
import { useTranslation } from 'react-i18next';

import { bubbleHeight } from './constants';
import { DEFAULT_TRANSLATIONS as translations } from '../models';

export interface BubbleProps {
  code: string;
  quantity: number;
  name: string;
  price: string | null;
  image: string;
  points: number | null;
  methodType: LastAddedItemMethod;
}

const BubbleWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  position: 'relative',
  width: theme.spacing(64),
  height: bubbleHeight,
  marginBottom: theme.spacing(2),
  padding: theme.spacing(1),
  border: `2px solid ${theme.palette.grey[100]}`,
  borderRadius: theme.spacing(2),
  background: theme.palette.common.white,
  boxShadow: SHADOW_LEVEL3,
  userSelect: 'none',
  pointerEvents: 'auto',
}));

const ImageWrapper = styled('div')(({ theme }) => ({
  flex: theme.spacing(0, 0, 16),
  width: theme.spacing(16),
  height: theme.spacing(16),
  marginRight: theme.spacing(2),
  overflow: 'hidden',
  borderRadius: theme.spacing(1, 0, 0, 1),
  background: theme.palette.grey[100],
  position: 'relative',
}));

const Image = styled('img')({
  width: '100%',
  maxWidth: '100%',
  height: '100%',
  maxHeight: '100%',
  objectFit: 'contain',
});

const ImagePlaceholder = styled(Products)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  width: '50%',
  height: 'auto',
  transform: 'translate(-50%, -50%)',
  color: theme.palette.grey[200],
}));

const Details = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flexStart',
  width: 216,
});

const Name = styled('div')(({ theme }) => ({
  width: '100%',
  paddingTop: theme.spacing(2),
  overflow: 'hidden',
  color: theme.palette.text.primary,
  fontSize: theme.typography.pxToRem(14),
  fontWeight: 700,
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

const Shade = styled('div')(({ theme }) => ({
  color: theme.palette.grey.A400,
  fontSize: theme.typography.pxToRem(12),
}));

const Shopping = styled('div')({
  display: 'flex',
  alignItems: 'center',
  marginTop: 'auto',
});

const Quantity = styled('div')(({ theme }) => ({
  flexGrow: 1,
  color: theme.palette.text.secondary,
  fontSize: theme.typography.pxToRem(12),
}));

const Price = styled('div')(({ theme }) => ({
  marginLeft: 'auto',
  color: theme.palette.text.primary,
  fontSize: theme.typography.pxToRem(14),
  fontWeight: 700,
}));

const Points = styled('div')(({ theme }) => ({
  position: 'absolute',
  right: theme.spacing(2),
  bottom: theme.spacing(2),
  width: theme.spacing(7),
  height: theme.spacing(7),
  borderRadius: '50%',
  background: theme.palette.offer.dark,
  color: theme.palette.common.white,
  fontSize: theme.typography.pxToRem(14),
  fontWeight: 'bold',
  lineHeight: theme.spacing(7),
  textAlign: 'center',
}));

function ignoreClickEvent(event: React.MouseEvent<HTMLElement>) {
  event.preventDefault();
  event.stopPropagation();
}

export const Bubble: VFC<BubbleProps> = ({
  name,
  code,
  quantity,
  price,
  image,
  points,
  methodType,
}) => {
  const { getTestId } = useTestId();
  const [translate] = useTranslation();
  const { BUY_FOR_MONEY, BUY_FOR_POINTS } = LastAddedItemMethod;

  return (
    <BubbleWrapper
      data-testid={getTestId('popup')}
      onClick={ignoreClickEvent}
    >
      <ImageWrapper>
        {image ? (
          <Image
            src={image}
            alt="product"
          />
        ) : (
          <ImagePlaceholder />
        )}
      </ImageWrapper>
      <Details>
        <Name>{name}</Name>
        <Shade>{code}</Shade>
        <Shopping>
          <Quantity>
            {translate(translations.quantity)}: {quantity}
          </Quantity>
          {methodType === BUY_FOR_MONEY && <Price>{price}</Price>}
          {methodType === BUY_FOR_POINTS && <Points>{points}</Points>}
        </Shopping>
      </Details>
    </BubbleWrapper>
  );
};
